import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import HideSourceIcon from '@mui/icons-material/HideSource';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  alpha,
  Box,
  Collapse,
  IconButton,
  Paper,
  Slide,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { type FC, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { formatDimensionsLine } from './ActivityDimensionChips.js';
import ActivityEventInlineClip from './ActivityEventInlineClip.js';
import ActivityRejectDialog from './ActivityRejectDialog.js';
import { type ActivityEvent, useReviewActivityEvent } from '../../api/activity.api.js';
import { activityEventRemoved } from '../../redux/reducers/pendingActivityEvents.js';
import { getPendingActivityEventsForPatient } from '../../redux/selectors.js';

interface Props {
  patientId: string;
  /** Refetch callback for any local list (per-stream log menu) that should mirror the review. */
  onReviewed?: () => void;
}

function formatTimeAgo(iso: string): string {
  const diff = DateTime.fromISO(iso).diffNow(['minutes', 'seconds']);
  const mins = Math.abs(Math.floor(diff.minutes));
  const secs = Math.abs(Math.floor(diff.seconds));
  if (mins >= 1) return `${mins} min ago`;
  return `${Math.max(secs, 1)} sec ago`;
}

const ActivityEventToast: FC<Props> = ({ patientId, onReviewed }) => {
  const dispatch = useDispatch();
  const pending = useSelector(useMemo(() => getPendingActivityEventsForPatient(patientId), [patientId]));
  const event: ActivityEvent | undefined = pending[0];

  const [clipOpen, setClipOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const review = useReviewActivityEvent(event?.id ?? '');

  if (!event) return null;

  const onAccept = () => {
    dispatch(activityEventRemoved(event.id));
    review.mutate(
      { verdict: 'accepted' },
      { onSuccess: () => onReviewed?.() },
    );
  };

  const onReject = () => {
    setRejectOpen(true);
  };

  // Hide without reviewing — the event still lives server-side and can be
  // reviewed later from the patient page table or the AI review panel.
  const onDismiss = () => {
    setClipOpen(false);
    dispatch(activityEventRemoved(event.id));
  };

  return (
    <>
      <Slide in direction="up" timeout={220} appear unmountOnExit>
        <Paper
          elevation={2}
          sx={{
            position: 'absolute',
            left: 8,
            right: 8,
            bottom: 64,
            zIndex: 8,
            px: 1.25,
            py: 0.75,
            borderRadius: 1,
            borderLeft: '2px solid',
            borderLeftColor: 'primary.main',
            bgcolor: (t) => alpha(t.palette.background.paper, 0.92),
            backdropFilter: 'blur(8px)',
            // When the clip is open, grow to fill the available tile height
            // (minus the 80px bottom inset + breathing room). Header stays at
            // its natural height, the clip flex-fills the rest. When the clip
            // is closed, the toast collapses to its content height.
            display: 'flex',
            flexDirection: 'column',
            ...(clipOpen
              ? { height: 'calc(100% - 80px)' }
              : { maxHeight: 'calc(100% - 80px)' }),
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesomeIcon
              fontSize="small"
              sx={{ color: 'primary.main', opacity: 0.85 }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}
              >
                AI · {formatTimeAgo(event.capturedAt)}
              </Typography>
              <Typography
                variant="body2"
                noWrap
                sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}
              >
                <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  {event.code.code}
                </Box>
                <Box
                  component="span"
                  sx={{ color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {event.code.description}
                </Box>
              </Typography>
              {event.dimensions && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}
                >
                  {formatDimensionsLine(event.dimensions)}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={0.25}>
              <Tooltip title={clipOpen ? 'Hide clip' : 'Preview clip'}>
                <IconButton size="small" onClick={() => setClipOpen((v) => !v)}>
                  {clipOpen ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Accept (write log with AI's code)">
                <IconButton
                  size="small"
                  color="success"
                  onClick={onAccept}
                  disabled={review.isLoading}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject — pick a different code">
                <IconButton
                  size="small"
                  color="error"
                  onClick={onReject}
                  disabled={review.isLoading}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Hide — review later from the patient page">
                <IconButton size="small" onClick={onDismiss} aria-label="Hide toast">
                  <HideSourceIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Collapse
            in={clipOpen}
            unmountOnExit
            sx={{
              ...(clipOpen && {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                '& .MuiCollapse-wrapper': { flex: 1, minHeight: 0 },
                '& .MuiCollapse-wrapperInner': { display: 'flex', minHeight: 0, height: '100%' },
              }),
            }}
          >
            <ActivityEventInlineClip eventId={event.id} open={clipOpen} />
          </Collapse>
        </Paper>
      </Slide>
      {rejectOpen && (
        <ActivityRejectDialog
          event={event}
          onClose={() => setRejectOpen(false)}
          onReviewed={(reviewed) => {
            setRejectOpen(false);
            dispatch(activityEventRemoved(reviewed.id));
            onReviewed?.();
          }}
        />
      )}
    </>
  );
};

export default ActivityEventToast;
